import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Modes } from '@constants/canvas/layers';
import { useProject } from '@context/Project/ProjectProvider';
import { useCanvasZoomActions } from '@hooks/canvas/useCanvasZoom';
import { useAppSelector } from '@hooks/store';
import useAssets from '@hooks/useAssets';
import useProjectId from '@hooks/useProjectId';
import { ROUTES } from '@router/routes';
import { selectProjectTabs } from '@store/slices/projectSlice';

type LeaveScenarioModeOptions = { withRedirect?: boolean } | void;

/**
 * Whether the current route is the what-if scenario side of a project, plus
 * the navigations that enter and leave it. Scenario mode is read the way
 * `useProjectId` reads it: the `scenario` path segment, or the
 * `?scenario=true` the questionnaire routes carry instead of a segment, so a
 * control mounted on a scenario-mode questionnaire reports the mode it is in.
 */
const useScenarioMode = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const { handleCloseDrawer } = useProject();
  const id = useProjectId();
  const projectsTabs = useAppSelector(selectProjectTabs);
  const { resetAsset } = useAssets();
  const { saveCurrentZoom } = useCanvasZoomActions();

  const scenarioMode =
    pathname.includes('scenario') || searchParams.get('scenario') === 'true';

  const enterScenarioMode = async ({
    forceProjectId,
    mode,
  }: {
    forceProjectId?: string;
    mode?: Modes | null;
  } = {}) => {
    await handleCloseDrawer();
    resetAsset();
    saveCurrentZoom();

    const isNoTabs = !projectsTabs[forceProjectId || id]?.scenarios.length;

    if (isNoTabs && !forceProjectId) {
      navigate(
        `${ROUTES.PROJECT.SCENARIOS.replace(':id', forceProjectId || id)}${
          mode ? `?mode=${mode}` : ''
        }`,
      );
    } else {
      navigate(
        `${ROUTES.PROJECT.SCENARIO.MODEL.replace(':id', forceProjectId || id)}${
          mode ? `?mode=${mode}` : ''
        }`,
      );
    }
  };

  const leaveScenarioMode = async ({
    withRedirect = true,
  }: LeaveScenarioModeOptions = {}) => {
    await handleCloseDrawer();
    resetAsset();
    saveCurrentZoom();

    if (withRedirect) {
      navigate(ROUTES.PROJECT.MODEL.replace(':id', id));
    }
  };

  return { scenarioMode, enterScenarioMode, leaveScenarioMode };
};

export default useScenarioMode;
